import * as React from 'react'

import { Query } from '@apollo/client/react/components'
import { inject, observer } from 'mobx-react'

import {
  IFormMaterial,
  IPermitTypeField,
  ISignature,
  PermitFieldType,
} from '~/client/graph'
import {
  GetSitePermitAggregationListDocument,
  IGetSitePermitAggregationListQuery,
  IGetSitePermitAggregationListQueryVariables,
} from '~/client/graph/operations/generated/SitePermits.generated'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { isLinkedWorkflowLikeField } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { MAX_PERMIT_TABLE_ROWS } from '~/client/src/shared/constants/permitTypeFieldsConstants'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import PermitField from '~/client/src/shared/models/PermitField'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import smoothScrollTo from '~/client/src/shared/utils/smoothScrollTo'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import CompanyProfilePreview from '../../../CompanyProfilePreview/CompanyProfilePreview'
import PermitFieldIcon from '../../../PermitFieldIcon/PermitFieldIcon'
import SitemapAttributeTag from '../../../SitemapAttributeTag/SitemapAttributeTag'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import SignaturePreview from './components/SignaturePreview'
import WorkflowFormAddFieldButton from './components/WorkflowFormAddFieldButton'
import PermitFieldRemoveIcon from './PermitFieldRemoveIcon'
import PermitFormAdditionalDateField from './PermitFormAdditionalDateField'
import PermitFormCompanyField from './PermitFormCompanyField'
import PermitFormInputField from './PermitFormInputField'
import PermitFormLinkedWorkflowField from './PermitFormLinkedWorkflowField'
import PermitFormLocationField from './PermitFormLocationFIeld'
import PermitFormMaterialField from './PermitFormMaterialField'
import PermitFormMeasureField from './PermitFormMeasureField'
import PermitFormPhoneNumberField from './PermitFormPhoneNumberField'
import PermitFormSelectField from './PermitFormSelectField'
import PermitFormSignatureField from './PermitFormSignatureField'
import PermitFormTextAreaField from './PermitFormTextAreaField'
import PermitFormUserField from './PermitFormUserField'

const tableFieldToComponentMap = {
  [PermitFieldType.AdditionalDate]: PermitFormAdditionalDateField,
  [PermitFieldType.InputText]: PermitFormInputField,
  [PermitFieldType.InputNumber]: PermitFormInputField,
  [PermitFieldType.Email]: PermitFormInputField,
  [PermitFieldType.PhoneNumber]: PermitFormPhoneNumberField,
  [PermitFieldType.InputTextArea]: PermitFormTextAreaField,
  [PermitFieldType.Measure]: PermitFormMeasureField,
  [PermitFieldType.Select]: PermitFormSelectField,
  [PermitFieldType.User]: PermitFormUserField,
  [PermitFieldType.Time]: PermitFormInputField,
  [PermitFieldType.LocationInformational]: PermitFormLocationField,
  [PermitFieldType.EquipmentInformational]: PermitFormLocationField,
  [PermitFieldType.CompanyInformational]: PermitFormCompanyField,
  [PermitFieldType.MaterialInformational]: PermitFormMaterialField,
  [PermitFieldType.LinkedWorkflow]: PermitFormLinkedWorkflowField,
  [PermitFieldType.Activity]: PermitFormLinkedWorkflowField,
  [PermitFieldType.PrimaryActivity]: PermitFormLinkedWorkflowField,
  [PermitFieldType.Signature]: PermitFormSignatureField,
}

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  parentInstanceId?: string
  parentValueIndex?: number

  eventsStore?: EventsStore
  projectDateStore?: ProjectDateStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  companiesStore?: CompaniesStore
  materialsStore?: MaterialsStore
  workflowTypesStore?: WorkflowTypesStore
}

const VALUE_SEPARATOR = ', '
const SCROLL_ANIMATION_TIME = 0.3

const _rowNumber = (rowNumber: number) => `Row #${rowNumber}`
const _addNewRow = 'Add a new row'
const _noFieldsInTable = 'There are no fields in the table'

@inject(
  'eventsStore',
  'projectDateStore',
  'projectMembersStore',
  'locationAttributesStore',
  'companiesStore',
  'materialsStore',
  'workflowTypesStore',
)
@observer
export default class PermitFormTableField extends React.Component<IProps> {
  private tableRef: HTMLDivElement

  public render() {
    const {
      typeField,
      isViewMode,
      store,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const areRowNumbersShown = this.tableRowValues.length > 1

    if (isViewMode) {
      return this.viewModeElement
    }

    return (
      <div className="permit-field-table col mt8 mb15 px8 brada4 ba-light-input-border bg-light-cool-grey">
        <span className="my10 px8 text bold extra-large">
          {typeField.caption}
        </span>
        <div
          className="table-row-containter overflow-auto"
          ref={this.setTableRef}
        >
          {this.tableRowValues.map((_, index) => (
            <div
              key={index}
              className="brada4 ba-light-input-border bg-white pa8 my20"
            >
              {areRowNumbersShown && (
                <div className="table-row-header row y-center sticky bg-white">
                  <span className="text bold large my10">
                    {_rowNumber(index + 1)}
                  </span>
                  <PermitFieldRemoveIcon
                    index={index}
                    onClick={this.removeRow}
                  />
                </div>
              )}
              {this.tableFields.length ? (
                this.tableFields.map(field => {
                  const TableFieldComponent =
                    tableFieldToComponentMap[field.type.toString()]

                  return (
                    <TableFieldComponent
                      key={`${field.id}_${index}`}
                      typeField={field}
                      store={store}
                      fieldsStore={fieldsStore}
                      tableId={typeField.id}
                      tableRowIndex={index}
                      parentInstanceId={parentInstanceId}
                      parentValueIndex={parentValueIndex}
                    />
                  )
                })
              ) : (
                <span className="text large bold px10">{_noFieldsInTable}</span>
              )}
            </div>
          ))}
        </div>
        <WorkflowFormAddFieldButton
          isShown={this.shouldShowAddRow}
          label={_addNewRow}
          className="tw:mt-2.5"
          onAddFieldClick={this.addNewRow}
        />
      </div>
    )
  }

  private get viewModeElement(): JSX.Element {
    const { typeField } = this.props

    return (
      <>
        <div className="row tw:gap-2.5 no-grow y-start py12">
          <PermitFieldIcon fieldName={typeField.type} />
          <div className="col pr6 overflow-hidden">
            <div className="text light large pb4 w-max-content">
              {typeField.caption}
            </div>
          </div>
        </div>

        <div className="permit-field-table view-mode relative overflow-auto mb15 full-width">
          <table className="pb10" cellPadding={10} cellSpacing={0}>
            <thead className="bg-light-cool-grey sticky">
              <tr>
                <th className="bt-light-grey br-light-grey bb-light-grey">#</th>
                {this.tableFields.map(field => (
                  <th
                    key={field.id}
                    className="field-header bt-light-grey br-light-grey bb-light-grey"
                  >
                    {field.caption}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {this.tableRowValues.map((tableRow, index) => (
                <tr key={index} className="bb-light-grey bt-light-grey">
                  <td className="br-light-grey bb-light-grey text bold large center">
                    {index + 1}
                  </td>
                  {this.tableFields.map(field => (
                    <td
                      key={`${field.id}_${index}`}
                      className="field-value-cell br-light-grey bb-light-grey"
                    >
                      {this.getFormattedCellValue(field, tableRow)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  private get tableRowValues(): PermitField[][] {
    const { typeField, fieldsStore, parentInstanceId, parentValueIndex } =
      this.props

    const values = fieldsStore.getFieldValues<PermitField[]>(
      typeField.id,
      null,
      null,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : [[]]
  }

  private get tableFields(): IPermitTypeField[] {
    const { fieldsStore, typeField } = this.props
    return fieldsStore.getTableFields(typeField.id)
  }

  private get shouldShowAddRow(): boolean {
    return (
      !!this.tableFields.length &&
      this.tableRowValues.length < MAX_PERMIT_TABLE_ROWS
    )
  }

  private addNewRow = () => {
    if (this.shouldShowAddRow) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.addNewFieldValue(
        typeField,
        [],
        parentInstanceId,
        parentValueIndex,
      )

      Promise.resolve().then(() => this.scrollToBottom())
    }
  }

  private removeRow = (rowIndex: number) => {
    const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
      this.props
    const { removeFieldValue, changeValidationState } = fieldsStore

    const rowFields = this.tableRowValues[rowIndex] || []

    this.tableFields.forEach(field => {
      this.getFieldValueFromCell(rowFields, field.id).forEach((_, vIdx) =>
        changeValidationState(
          field.id,
          vIdx,
          true,
          rowIndex,
          parentInstanceId,
          parentValueIndex,
        ),
      )
    })

    removeFieldValue(typeField.id, rowIndex, parentInstanceId, parentValueIndex)
  }

  private getFieldValueFromCell(fields: PermitField[], typeFieldId: string) {
    return fields.find(({ fieldId }) => fieldId === typeFieldId)?.values || []
  }

  private getFormattedCellValue(
    field: IPermitTypeField,
    allCells: PermitField[],
  ): string | JSX.Element {
    const {
      projectDateStore: {
        getWeekdayMonthDayAndYearToDisplay,
        combineISODateTime,
        getTimeToDisplay,
      },
      projectMembersStore,
      locationAttributesStore,
      companiesStore,
      workflowTypesStore,
    } = this.props

    const values = this.getFieldValueFromCell(allCells, field.id)

    if (values.every(v => !v)) {
      return NO_VALUE
    }

    if (field.type === PermitFieldType.CompanyInformational) {
      return (
        <div className="row overflow-hidden">
          {values.map(id => (
            <CompanyProfilePreview
              key={id}
              company={companiesStore.getCompanyById(id)}
              isDeactivatedShown
            />
          ))}
        </div>
      )
    }

    if (field.type === PermitFieldType.User) {
      return (
        <div className="row overflow-hidden">
          {values
            .filter(id => projectMembersStore.hasById(id))
            .map(usrId => (
              <UserProfilePreview
                key={usrId}
                user={projectMembersStore.getById(usrId)}
              />
            ))}
        </div>
      )
    }
    if (
      field.type === PermitFieldType.LocationInformational ||
      field.type === PermitFieldType.EquipmentInformational
    ) {
      return (
        <div className="row overflow-hidden">
          {values.map(({ id }, index) => {
            const location = locationAttributesStore.getById(id)
            if (location) {
              return (
                <SitemapAttributeTag
                  key={`${location.id}_${index}`}
                  shouldShowAsTag={false}
                  dataObject={location}
                  className="row"
                  contentContainerClassName="text extra-large ellipsis parent-tag"
                >
                  {location.name}
                </SitemapAttributeTag>
              )
            }
          })}
        </div>
      )
    }
    if (field.type === PermitFieldType.MaterialInformational) {
      const { materialsStore } = this.props
      return (
        <div className="col">
          {values.map((material: IFormMaterial, index) => {
            const materialInstance = materialsStore.getInstanceById(
              material.materialId,
            )
            if (materialInstance) {
              const displayText = material.procurementId
                ? `${materialInstance.productName} ${material.procurementId}`
                : materialInstance.productName
              return (
                <span
                  key={`${material.materialId}_${index}`}
                  className="text extra-large"
                >
                  {displayText}
                </span>
              )
            }
          })}
        </div>
      )
    }
    if (field.type === PermitFieldType.Measure) {
      const measureValues = values.filter(mv => mv?.value || mv?.units)
      return !measureValues.length
        ? NO_VALUE
        : measureValues.map(v => `${v.value} ${v.units}`).join(VALUE_SEPARATOR)
    }
    if (field.type === PermitFieldType.AdditionalDate) {
      return values
        .map(v => getWeekdayMonthDayAndYearToDisplay(v))
        .join(VALUE_SEPARATOR)
    }
    if (field.type === PermitFieldType.Time) {
      return values
        .map(v => getTimeToDisplay(combineISODateTime(undefined, v)))
        .join(VALUE_SEPARATOR)
    }

    if (isLinkedWorkflowLikeField(field.type)) {
      return (
        <Query<
          IGetSitePermitAggregationListQuery,
          IGetSitePermitAggregationListQueryVariables
        >
          query={GetSitePermitAggregationListDocument}
          variables={{
            filter: {
              projectId: this.props.eventsStore.appState.activeProject.id,
              ids: values,
              withAllDrafts: true,
            },
            offset: 0,
            limit: values?.length || 1,
          }}
        >
          {({ loading, error, data }) => {
            if (loading || !data || error) return null

            return (
              <>
                {data?.sitePermitsAggregation?.data?.map(permitDto => {
                  const permit = Workflow.fromDto(permitDto)
                  return (
                    <React.Fragment key={permit.id}>
                      <span>{permit.title}</span>
                      <span>{permit.getCaption(workflowTypesStore)}</span>
                    </React.Fragment>
                  )
                })}
              </>
            )
          }}
        </Query>
      )
    }

    if (field.type === PermitFieldType.Signature) {
      return (
        <div className="col">
          {values.map((signature: ISignature, index) => (
            <SignaturePreview
              key={index}
              signature={signature}
              isUserStampEnabled={field.isUserStampEnabled}
            />
          ))}
        </div>
      )
    }

    return values.join(VALUE_SEPARATOR)
  }

  private setTableRef = (ref: HTMLDivElement) => {
    this.tableRef = ref
  }

  private scrollToBottom = () => {
    if (!this.tableRef) {
      return
    }

    const { scrollHeight, clientHeight } = this.tableRef
    smoothScrollTo(
      this.tableRef,
      scrollHeight - clientHeight,
      0,
      SCROLL_ANIMATION_TIME,
    )
  }
}
